import React, { useEffect, useMemo, useState } from "react";
import AppUpdater from 'services/app-updater'
import Button from 'theme/Button'
import { useInterval } from 'utils/hooks/use-interval'
import LoadingComponent from 'components/atoms/Loading'
import ThemedBlock from 'components/atoms/ThemedBlock'
import Text from "theme/Text";
import i18n from 'utils/i18n'
import { StyleSheet, View } from 'react-native'
import CacheStorage from 'utils/cache/CacheStorage'
import HelperFileCache from 'utils/HelperFileCache'
import Ajax from 'utils/request/ajax'
import HelperPlatform from 'utils/helper-platform'
import HelperVersion from 'utils/helper-version'
import _ from "lodash";
import HelperDevelopment from 'utils/helper-development'
import { configClient } from 'components/provider/ConfigProvider'
import { openUrl } from 'utils/helper-url'
import HelperDate, { dateFormat } from 'utils/helper-date'

const hideMaximumNumberOfHours = 12; // the maximum amount of hours the update popup will be hidden
const updateInterval = 60 * 5; // check for updates every 5 minutes

export const UPDATE_VARIANT = {
	BUTTON: 'button',
	ONLY_UPDATE: 'only_update'
}

const IndexUpdateSceneBlock = ({ isFocused = false, variant = UPDATE_VARIANT.BUTTON }) => {

	const [ isUpdateAvailable, setIsUpdateAvailable ] = useState( false );
	const [ isCheckingUpdate, setIsCheckingUpdate ] = useState( false);

	const [ isBuildAvailable, setIsBuildAvailable ] = useState( false );
	const [ buildUrl, setBuildUrl ] = useState( false );
	const [ isCheckingBuild, setIsCheckingBuild ] = useState( false);

	const [ isUpdating, setIsUpdating ] = useState( false);
	const [ lastCheckedUpdateDateTime, setLastCheckedUpdateDateTime ] = useState( null );


	const [ isHidden, setIsHidden ] = useState( false );
	const [ isHiddenSince, setIsHiddenSince ] = useState( null );

	const checkForUpdates = () => {
		// if we know there's an update we don't need to check for it again
		// or if we're already checking we don't run it again
		// or if the page is not focused do not check for updates
		if( isUpdateAvailable || isCheckingUpdate || !isFocused ){
			return;
		}

		const appUpdater = new AppUpdater();

		setIsCheckingUpdate( true );

		appUpdater
			.check()
			.then( () => {
				if( appUpdater.updateIsAvailable  !== isUpdateAvailable ){
					setIsUpdateAvailable( appUpdater.updateIsAvailable );
				}
				setIsCheckingUpdate( false );
				setLastCheckedUpdateDateTime( HelperDate.date().format( dateFormat.DATE_TIME ) );
			});
	}

	const checkForBuilds = () => {
		if( isBuildAvailable || isCheckingBuild || !isFocused ){
			return;
		}

		let url = configClient.url() +'/builds/';

		switch( true ){
			case HelperPlatform.isIOS():
				url += 'ios';
				break;

			default:
				url += 'android';
				break;
		}
		
		Ajax.request( url, {
			onStart: () => {
				setIsCheckingBuild( true );
			},
			onSuccess: ( result ) => {
				setIsCheckingBuild( false );
				const installedVersion = HelperDevelopment.isDev() ? '2.1.0' : HelperVersion.getNativeAppVersion();
				const availableVersion = _.get( result, 'version', '0.0.0' );

				// check if installed version is lower than available version
				if( HelperVersion.compareVersions( installedVersion, availableVersion ) < 0 ){
					setIsBuildAvailable( true );
					setBuildUrl( _.get( result, 'url', false ) );
				}
			},
			onError: ( result ) => {
				setIsCheckingBuild( false );
			},
		});
	}

	const backgroundProcess = () => {
		if( isFocused && ( lastCheckedUpdateDateTime === null || HelperDate.date().diff( HelperDate.date( lastCheckedUpdateDateTime ), 'second' ) > updateInterval ) ){
			if( !isCheckingUpdate ) {
				checkForUpdates();
			}

			if( !isBuildAvailable ) {
				checkForBuilds();
			}
		}

		if( isHiddenSince !== null && HelperDate.date().diff( isHiddenSince, 'hours' ) > hideMaximumNumberOfHours  ){
			setIsHidden( false );
			setIsHiddenSince( null );
		}
	}

	const updateApp = () => {
		setIsUpdating( true );

		CacheStorage.clearCache( { restart: false } ).then( () => {
			HelperFileCache.drop().then( () => {
				const appUpdater = new AppUpdater();
				appUpdater.fetch().then( () => {
					appUpdater.reload()
				});
			});
		});
	}

	const updateBuild = () => {
		openUrl( buildUrl );
	}

	const hide = () => {
		setIsHidden( true );
		setIsHiddenSince( HelperDate.date() );
	}

	useEffect( () => {
		if( isFocused && ( lastCheckedUpdateDateTime === null || HelperDate.date().diff( HelperDate.date( lastCheckedUpdateDateTime ), 'seconds' ) > updateInterval ) ){
			if( !isCheckingUpdate ) {
				checkForUpdates();
			}

			if( !isBuildAvailable ) {
				checkForBuilds();
			}
		}
	}, [ isFocused ] );

	// Set an interval to check if we need to check for updates each minute (when the home page is focused)
	useInterval( backgroundProcess, 1000 * 60 );

	const content = useMemo( () => {
		if( isHidden === false ) {
			if( isBuildAvailable ) {
				return (
					<UpdateBlockView
						variant={ variant }
						text={ i18n.t( 'pages.home.build.available') }
						onPressHide={ hide }
						onPressUpdate={ updateBuild }
						updateText={ i18n.t( 'pages.home.build.goto_app_store') }
					/>
				)
			}
			
			if( isUpdateAvailable ) {
				return (
					<UpdateBlockView
						variant={ variant }
						text={ i18n.t( 'pages.home.update.available') }
						onPressHide={ hide }
						onPressUpdate={ updateApp }
					/>
				)
			}
		}
		
		return null;
	}, [ isHidden, isUpdateAvailable, isBuildAvailable ] );

	if( isUpdating ) {
		return (
			<LoadingComponent fullscreen={ true } />
		);
	}

	return content;
}

const UpdateBlockView = ({ variant, onPressHide, hideText, onPressUpdate, updateText, text }) => {
	
	return (
		<ThemedBlock
			shadow
			mediumPadding>
			
			{ variant === UPDATE_VARIANT.BUTTON && (
				<Text style={ styles.textMarginBottom8FontSize14 }>{ text }</Text>
			)}
			
			<View style={ styles.buttons}>
				{ variant === UPDATE_VARIANT.BUTTON && (
					<View
						style={ styles.button }>
						
						<Button
							onPress={ onPressHide }
							size={ 'small' }
							appearance={ "ghost"}>
							
							{ hideText ?? i18n.t( 'pages.home.update.hide')}
						</Button>
					</View>
				)}
				
				<View
					style={ styles.button }>
					
					<Button
						onPress={ onPressUpdate }
						size={ 'small' }>
						
						{ updateText ?? i18n.t( 'pages.home.update.update_now') }
					</Button>
				</View>
			</View>
		</ThemedBlock>
	)
}

const styles = StyleSheet.create({
	textMarginBottom8FontSize14: {
		marginBottom: 8,
		fontSize: 14,
	},
	buttons: {
		flexDirection: 'row'
	},
	button: {
		flex: 1
	}
});

export default IndexUpdateSceneBlock
